/**
 * Tower catalogue: ten archetypes, each with three five-tier upgrade paths.
 *
 * The BTD6 lesson this file is trying to learn is that a path is only worth
 * having if it changes *how the tower plays*, not how big its numbers are. So
 * every tower here has one path that scales the thing it already does, one that
 * bends it into a different role, and one that gives it an answer to a problem
 * it could not previously solve. A Turret that took the rail path stops caring
 * about swarms; a Turret that took the rate path stops caring about armour.
 *
 * ## Effect encoding
 *
 * `UpgradeNode.effects` in `core/types.ts` is `Partial<TowerStats> & { grants }`.
 * That covers additive stat deltas and ability strings but has nowhere to put a
 * multiplier, so this file widens it to {@link TowerEffects}, which adds a `mul`
 * bag. The widening is structural, not a cast: `node()` takes a typed parameter,
 * and TypeScript only runs excess-property checks on fresh object literals.
 * `Upgrades.applyUpgrades` narrows back. See the report note about folding `mul`
 * into `core/types.ts` properly.
 *
 * Numeric fields on `effects` are **additive deltas** against the base stat.
 * `mul` entries are **multipliers**, all of which apply after every addition.
 *
 * ## Two balance rules the numbers here are bound by
 *
 * 1. **`armourPierce` is capped near 3.** It multiplies damage *before* flat
 *    armour is subtracted (`Projectiles.resolve`), so it is a raw damage
 *    multiplier against every archetype with armour — which is all but five.
 *    Left uncapped it reached 11.4 on a tier-5 Sniper, which made armour a
 *    *weakness*: an armour-24 siege unit took more damage than an unarmoured
 *    swarmling. No path now exceeds 3.5 total. The flat damage on those same
 *    capstones rose ~1.8x in exchange, so the nodes still read as a payoff.
 * 2. **Tier 4 costs 2.2x and tier 5 costs 5.0x** what a naive curve would ask.
 *    A finished twenty-four slot board is `Balance.MAX_BOARD_COST`, about 2.57
 *    million, against 677,000 before. That number is the late-game cash sink:
 *    `Economy` is tuned so a hundred rounds of income lands just under it, so
 *    the tree cannot be exhausted and the last purchase is still a choice.
 *    See docs/BALANCE.md §4.3.
 *
 * ## Ability strings
 *
 * `grants` carries behaviour that does not fit a scalar stat. The grammar is
 * `verb:arg[:arg]`, parsed once in `TowerSystem`/`Projectiles`, never elsewhere:
 *
 * | grant | meaning |
 * |---|---|
 * | `chain:N` / `chainFalloff:F` | extra lightning jumps, damage retained per jump |
 * | `stun:SEC` / `stunChance:P` | stun duration and probability per hit |
 * | `slow:FACTOR:SEC` | multiply enemy speed by FACTOR for SEC seconds |
 * | `shatter:MUL` | damage multiplier against a slowed or stunned target |
 * | `dot:DPS:SEC` | damage over time in the tower's own damage type |
 * | `napalm:DPS:SEC:RADIUS` | ground fire pool left by an explosion |
 * | `stacks:N` | maximum independent DOT stacks on one enemy |
 * | `spreadOnDeath:RADIUS:FRACTION` | re-apply the DOT to neighbours when the host dies |
 * | `shieldMelt:MUL` | bonus multiplier against the shield pool only |
 * | `cluster:N:FRACTION` | bomblets released on detonation |
 * | `prox:RADIUS` | proximity fuse: detonate near a target instead of on contact |
 * | `cone:RADIANS` | half-angle of the shotgun spread |
 * | `ramp:PER_SEC:MAX` | beam damage ramp while held on one target |
 * | `split:N` | additional independent beams |
 * | `pull:FORCE` | gravity well strength in px/s^2 at the centre |
 * | `detonate:FRACTION:RADIUS` | collapse damage when the well expires |
 * | `global` | ignore the range check entirely |
 * | `aura:damage\|rate\|cash:VALUE` | Pylon support aura, VALUE is a fractional bonus |
 * | `aura:slow:FACTOR` | Cryo permafrost ring, applied to enemies not to towers |
 * | `ability:NAME:COOLDOWN` | player-activated ability |
 */

import type {
  DamageType, TowerArchetype, TowerStats, UpgradeNode,
} from '@/core/types';

// ────────────────────────────────────────────────────────── effect widening ──

/** Every `TowerStats` key whose value is a number. */
export type NumericStat = Exclude<keyof TowerStats, 'damageType'>;

/**
 * Authoring shape for `UpgradeNode.effects`. Structurally a superset of the
 * core type, so it assigns without a cast anywhere except the narrowing read in
 * `Upgrades.applyUpgrades`.
 */
export interface TowerEffects extends Partial<TowerStats> {
  grants?: string[];
  /** Multipliers applied after the whole additive pass. */
  mul?: Partial<Record<NumericStat, number>>;
}

/** Node constructor. Positional so the tables below stay readable at a glance. */
function n(
  name: string,
  icon: string,
  cost: number,
  description: string,
  effects: TowerEffects,
): UpgradeNode {
  return { name, icon, cost, description, effects };
}

function stats(
  damage: number,
  damageType: DamageType,
  fireRate: number,
  range: number,
  pierce: number,
  projectileSpeed: number,
  splash: number,
  armourPierce = 1,
  critChance = 0,
  critMultiplier = 1.5,
): TowerStats {
  return {
    damage, damageType, fireRate, range, pierce,
    projectileSpeed, splash, armourPierce, critChance, critMultiplier,
  };
}

// ──────────────────────────────────────────────────────────────── behaviour ──

/** How a tower converts a firing event into damage. */
export type FireKind = 'projectile' | 'beam' | 'ground' | 'cone' | 'chain' | 'well' | 'none';

/**
 * Per-archetype firing behaviour and sprite binding. Not part of `TowerStats`
 * because none of it is upgradeable — upgrades change the numbers, never the
 * verb. `TowerRenderer` reads `sprite`/`plate`, the sim reads the rest.
 */
export interface TowerBehaviour {
  kind: FireKind;
  /** Half-angle of the spread cone in radians. Only read for `cone`. */
  cone: number;
  /** Base lightning jumps. Only read for `chain`. */
  chain: number;
  /** Damage retained per jump, 0..1. */
  chainFalloff: number;
  /** Homing acceleration factor, 0 = ballistic. */
  homing: number;
  /** Barrel length in pixels; the muzzle flash and spawn point sit here. */
  muzzle: number;
  /** Seconds the barrel takes to swing a full turn. Lower = snappier aim. */
  turnSeconds: number;
  /** Barrel sprite tile in the tower atlas. */
  sprite: number;
  /** Base plate variant, 0..3, chosen by weight class. */
  plate: number;
  /** Projectiles from this tower request a trail ribbon. */
  trailed: boolean;
}

// ─────────────────────────────────────────────────────────────────── turret ──

const TURRET: TowerArchetype = {
  id: 'turret',
  name: 'Turret',
  icon: 'octagon-times-solid',
  colour: 'yellow',
  cost: 200,
  footprint: 7,
  blurb: 'Belt-fed autocannon. Cheap, honest, and still relevant at round 90.',
  base: stats(4, 'kinetic', 1.6, 96, 1, 320, 0),
  paths: [
    [
      n('Autoloader', 'refresh-solid', 90,
        'A second feed tray means the gun never waits on a human.',
        { fireRate: 0.5 }),
      n('Belt Feed', 'bars-solid', 180,
        'Linked rounds. The belt only stops when the round does.',
        { fireRate: 0.7, damage: 1 }),
      n('Dual Cannons', 'bracket-square-solid', 550,
        'Two barrels, one trigger, twice the noise complaint.',
        { fireRate: 1.4, damage: 2 }),
      n('Gatling Spool', 'spinner-solid', 3100,
        'Spins up as it fires and refuses to spin down.',
        { fireRate: 3.2, damage: 2, mul: { fireRate: 1.15 } }),
      n('Hurricane Drum', 'spinner-third-solid', 24000,
        'A wall of brass on the floor and a wall of lead in the air.',
        { fireRate: 6.0, damage: 6, pierce: 1, mul: { fireRate: 1.25 } }),
    ],
    [
      n('Long Barrel', 'arrow-right-solid', 80,
        'More barrel, more time for the spin to settle.',
        { range: 22, projectileSpeed: 60 }),
      n('Rangefinder', 'search-solid', 190,
        'Laser ranging trims the guesswork out of every lead.',
        { range: 30, critChance: 0.06 }),
      n('Match Grade', 'badge-check-solid', 600,
        'Hand-weighed powder charges. Every round lands where the last one did.',
        { damage: 3, critChance: 0.10, critMultiplier: 0.4 }),
      n('Ballistic Computer', 'laptop-code-solid', 3500,
        'Solves the firing problem before the target finishes moving.',
        { range: 40, critChance: 0.14, critMultiplier: 0.5, mul: { damage: 1.15 } }),
      n('Deadeye Protocol', 'eye-solid', 26000,
        'It has already decided where you will be. You are just catching up.',
        { range: 52, damage: 10, critChance: 0.25, critMultiplier: 1.1, mul: { critMultiplier: 1.2 } }),
    ],
    [
      n('Hardened Slugs', 'disc-solid', 120,
        'Solid shot. Goes in one side and keeps the appointment on the other.',
        { pierce: 1, damage: 2 }),
      n('Sabot Rounds', 'location-pin-solid', 260,
        'Discards the jacket at the muzzle and arrives as a needle.',
        { damage: 3, armourPierce: 0.25, projectileSpeed: 120 }),
      n('Rail Accelerator', 'bolt-solid', 800,
        'Magnetic rails add a kilometre a second and a smell of ozone.',
        { damage: 8, pierce: 2, projectileSpeed: 380, mul: { fireRate: 0.85 } }),
      n('Coilgun', 'chart-network-solid', 4600,
        'Stacked coils. The slug leaves before the report does.',
        { damage: 27, pierce: 3, projectileSpeed: 500, armourPierce: 0.35 }),
      n('Mass Driver', 'globe-solid', 35000,
        'Punches a corridor through the entire column and keeps going.',
        { damage: 99, pierce: 12, projectileSpeed: 900, armourPierce: 0.5, mul: { damage: 1.2 } }),
    ],
  ],
};

// ─────────────────────────────────────────────────────────────────── mortar ──

const MORTAR: TowerArchetype = {
  id: 'mortar',
  name: 'Mortar',
  icon: 'location-pin-solid',
  colour: 'red',
  cost: 420,
  footprint: 8,
  blurb: 'Lobs shells at a marked patch of ground. Does not care what walks over it.',
  base: stats(18, 'explosive', 0.45, 200, 1, 140, 30, 1.1),
  paths: [
    [
      n('Twin Shells', 'bracket-round-solid', 180,
        'Two tubes fire as one. The ground gets it twice.',
        { grants: ['cluster:2:0.5'] }),
      n('Bomblet Casing', 'ellipses-horizontal-solid', 360,
        'The casing is the payload. It comes apart on purpose.',
        { grants: ['cluster:4:0.45'], damage: 4 }),
      n('Cluster Munition', 'grid-solid', 1100,
        'Six sub-munitions, scattered wide enough to be unfair.',
        { grants: ['cluster:6:0.5'], damage: 8, splash: 4 }),
      n('MIRV', 'sitemap-solid', 5950,
        'Independently targeted re-entry. Each bomblet picks its own victim.',
        { grants: ['cluster:9:0.55'], damage: 16, mul: { fireRate: 1.15 } }),
      n('Saturation Barrage', 'table-solid', 45000,
        'Fourteen impacts inside two seconds. The map redraws itself.',
        { grants: ['cluster:14:0.6'], damage: 40, splash: 8, mul: { damage: 1.2 } }),
    ],
    [
      n('Bigger Charge', 'plus-solid', 200,
        'More filler, wider crater, same tube.',
        { splash: 10, damage: 4 }),
      n('Shaped Blast', 'shapes-solid', 400,
        'Directed liner focuses the wave outward instead of into the dirt.',
        { splash: 14, damage: 8 }),
      n('Concussive Wave', 'sound-on-solid', 1250,
        'The overpressure staggers anything still standing at the rim.',
        { splash: 20, damage: 12, grants: ['stun:0.25', 'stunChance:0.35'] }),
      n('Fuel-Air Mix', 'cloud-fog-solid', 7050,
        'Aerosolise first, ignite second. Physics does the rest.',
        { splash: 34, damage: 30, mul: { splash: 1.15 } }),
      n('Seismic Detonation', 'globe-americas-solid', 55000,
        'Registers on instruments three maps away.',
        { splash: 56, damage: 80, grants: ['stun:0.7', 'stunChance:1.0'], mul: { splash: 1.25, damage: 1.15 } }),
    ],
    [
      n('Incendiary Filler', 'fire-solid', 220,
        'Swaps high explosive for something that keeps burning.',
        { grants: ['napalm:6:3:20'], damageType: 'explosive' }),
      n('Sticky Fire', 'paperclip-solid', 430,
        'Gels the mixture so it rides along instead of pooling.',
        { grants: ['napalm:12:4:24', 'dot:8:3'] }),
      n('Napalm Wells', 'cloud-rain-solid', 1400,
        'Leaves a lake of fire that outlives the shell by half a round.',
        { grants: ['napalm:26:6:32'], damage: 10 }),
      n('Firestorm', 'sun-solid', 7900,
        'Enough heat that the fires start pulling air toward themselves.',
        { grants: ['napalm:60:7:40'], damage: 22, mul: { fireRate: 1.2 } }),
      n('Scorched Earth', 'exclamation-triangle-solid', 60000,
        'The track is no longer terrain. It is a hazard with a timer.',
        { grants: ['napalm:150:9:52', 'dot:40:5'], damage: 45, mul: { splash: 1.2 } }),
    ],
  ],
};

// ──────────────────────────────────────────────────────────────────── tesla ──

const TESLA: TowerArchetype = {
  id: 'tesla',
  name: 'Tesla Coil',
  icon: 'bolt-solid',
  colour: 'blue',
  cost: 380,
  footprint: 7,
  blurb: 'Arcs to the nearest conductor, then the next. Loves a tight formation.',
  base: stats(7, 'energy', 1.1, 88, 1, 900, 0),
  paths: [
    [
      n('Second Arc', 'branch-solid', 160,
        'The discharge finds a second path before the first one closes.',
        { grants: ['chain:2', 'chainFalloff:0.72'] }),
      n('Forked Discharge', 'fork-solid', 330,
        'Splits at the tip. Both halves are still lethal.',
        { grants: ['chain:4', 'chainFalloff:0.78'], damage: 2, fireRate: 0.3 }),
      n('Arc Cascade', 'share-alt-solid', 1000,
        'Each jump ionises the air for the next one.',
        { grants: ['chain:7', 'chainFalloff:0.84'], damage: 4, fireRate: 0.5 }),
      n('Lightning Web', 'chart-network-solid', 5500,
        'Twelve targets share one bolt and none of them enjoy it.',
        { grants: ['chain:12', 'chainFalloff:0.9'], damage: 8, fireRate: 0.8, mul: { fireRate: 1.15 } }),
      n('Storm Nexus', 'wifi-solid', 42500,
        'Chains until it runs out of swarm. It rarely runs out of swarm.',
        { grants: ['chain:24', 'chainFalloff:0.96'], damage: 20, fireRate: 1.4, range: 24, mul: { damage: 1.15 } }),
    ],
    [
      n('Static Shock', 'exclaimation-solid', 190,
        'A brief lockup while the target reboots its legs.',
        { grants: ['stun:0.15', 'stunChance:0.30'] }),
      n('Paralysis Field', 'pause-solid', 380,
        'Holds the muscle contracted instead of merely surprising it.',
        { grants: ['stun:0.3', 'stunChance:0.5'], damage: 2 }),
      n('Overload Clamp', 'lock-solid', 1150,
        'Latches onto the target and does not let the charge dissipate.',
        { grants: ['stun:0.5', 'stunChance:0.75'], damage: 5, fireRate: 0.4, mul: { damage: 1.1 } }),
      n('EMP Burst', 'times-square-solid', 6400,
        'Every enemy in range drops mid-stride. Armour is not insulation.',
        { grants: ['stun:0.8', 'stunChance:1.0', 'ability:emp:22'], damage: 10, fireRate: 0.5 }),
      n('Systemic Shutdown', 'octagon-times-solid', 48000,
        'They stop. All of them. You get to decide when they start again.',
        { grants: ['stun:1.6', 'stunChance:1.0', 'ability:emp:14'], damage: 26, fireRate: 0.9, range: 20 }),
    ],
    [
      n('Capacitor Bank', 'archive-solid', 200,
        'Dumps a stored charge on top of the working one.',
        { damage: 5 }),
      n('Voltage Ramp', 'trending-solid', 420,
        'Steps the potential up every discharge until something gives.',
        { damage: 11, mul: { damage: 1.1 } }),
      n('Superconductor', 'brightness-low-solid', 1300,
        'Chilled windings. No resistance, no losses, no mercy.',
        { damage: 26, mul: { fireRate: 1.2, damage: 1.1 } }),
      n('Plasma Arc', 'star-crescent-solid', 7500,
        'The air itself becomes the conductor and the conductor becomes a weapon.',
        { damage: 62, splash: 12, mul: { damage: 1.15 } }),
      n('Zeus Protocol', 'crown-solid', 57500,
        'One strike, from directly above, no discussion.',
        { damage: 270, splash: 22, armourPierce: 0.45, mul: { damage: 1.25 } }),
    ],
  ],
};

// ───────────────────────────────────────────────────────────────────── cryo ──

const CRYO: TowerArchetype = {
  id: 'cryo',
  name: 'Cryo Projector',
  icon: 'cloud-fog-solid',
  colour: 'cyan',
  cost: 300,
  footprint: 7,
  blurb: 'Barely scratches anything. Makes everything else on the map twice as good.',
  base: stats(3, 'energy', 1.0, 92, 4, 260, 22),
  paths: [
    [
      n('Deep Chill', 'brightness-low-solid', 130,
        'Drops core temperature far enough that movement gets expensive.',
        { grants: ['slow:0.72:2.0'] }),
      n('Flash Freeze', 'camera-solid', 280,
        'Instant phase change on contact. Nothing walks that off.',
        { grants: ['slow:0.58:2.4'], damage: 2 }),
      n('Ice Lock', 'lock-alt-solid', 870,
        'Not slowed. Stopped, briefly, and embarrassed about it.',
        { grants: ['slow:0.42:3.0', 'stun:0.35', 'stunChance:0.4'], damage: 3 }),
      n('Absolute Zero', 'moon-solid', 4850,
        'The molecular argument ends. Everything in the cone agrees.',
        { grants: ['slow:0.26:3.6', 'stun:0.6', 'stunChance:0.7'], damage: 8, splash: 8 }),
      n('Cryostasis Cage', 'bracket-curly-solid', 39000,
        'Holds a whole group mid-step like a photograph you can shoot at.',
        { grants: ['slow:0.14:4.5', 'stun:1.2', 'stunChance:1.0', 'ability:stasis:26'], damage: 16, splash: 14 }),
    ],
    [
      n('Frost Fracture', 'text-slash-solid', 150,
        'Cold makes plating brittle. Brittle plating stops being plating.',
        { grants: ['shatter:1.25'] }),
      n('Brittle Armour', 'strike-through-solid', 320,
        'Every hit on a chilled target strips a little more structure.',
        { grants: ['shatter:1.5'], armourPierce: 0.2 }),
      n('Shatterpoint', 'divider-solid', 970,
        'Finds the crystal boundary and hits exactly there.',
        { grants: ['shatter:1.9'], damage: 10, armourPierce: 0.25 }),
      n('Kinetic Shatter', 'shapes-solid', 5700,
        'The frozen fragments become shrapnel for whatever is behind.',
        { grants: ['shatter:2.4'], damage: 24, splash: 16, damageType: 'kinetic' }),
      n('Zero-Point Collapse', 'times-circle-solid', 44000,
        'A frozen target does not die so much as stop being assembled.',
        { grants: ['shatter:3.4'], damage: 60, splash: 26, armourPierce: 0.45, mul: { damage: 1.2 } }),
    ],
    [
      n('Frost Aura', 'cloud-fog-solid', 170,
        'A permanent cold ring. No shot required, no ammunition consumed.',
        { grants: ['aura:slow:0.88'] }),
      n('Cold Front', 'cloud-download-solid', 350,
        'The chill spreads with the tower instead of with the projectile.',
        { grants: ['aura:slow:0.80'], range: 14 }),
      n('Permafrost', 'grid-solid', 1050,
        'The ground stays frozen. Everything crossing it pays rent.',
        { grants: ['aura:slow:0.70'], range: 20, damage: 4 }),
      n('Blizzard Field', 'cloud-rain-solid', 6150,
        'Whiteout conditions inside the ring, with damage attached.',
        { grants: ['aura:slow:0.58', 'aura:damage:0.08'], range: 26, damage: 10 }),
      n('Fimbulwinter', 'star-crescent-solid', 47000,
        'Three winters, no summer between them, all inside your range circle.',
        { grants: ['aura:slow:0.44', 'aura:damage:0.15'], range: 40, damage: 24, mul: { range: 1.1 } }),
    ],
  ],
};

// ──────────────────────────────────────────────────────────────────── laser ──

const LASER: TowerArchetype = {
  id: 'laser',
  name: 'Laser Emitter',
  icon: 'brightness-high-solid',
  colour: 'magenta',
  cost: 520,
  footprint: 7,
  blurb: 'Hitscan beam that gets angrier the longer it stays on one target.',
  base: stats(5, 'energy', 6.0, 110, 1, 0, 0, 1.2),
  paths: [
    [
      n('Focusing Lens', 'search-solid', 210,
        'Tightens the waist of the beam. Same power, smaller hole.',
        { grants: ['ramp:0.15:1.8'], damage: 1 }),
      n('Heat Sink', 'brightness-low-solid', 440,
        'Lets the emitter hold the burn instead of blinking to save itself.',
        { grants: ['ramp:0.25:2.6'], mul: { fireRate: 1.15 } }),
      n('Thermal Runaway', 'trending-solid', 1300,
        'Every tick makes the next tick worse for whoever is standing there.',
        { grants: ['ramp:0.4:4.0'], damage: 4 }),
      n('Sustained Burn', 'fire-solid', 7250,
        'Twelve seconds on target and the beam is doing six times its opening damage.',
        { grants: ['ramp:0.55:6.5'], damage: 10, mul: { damage: 1.1 } }),
      n('Solar Lance', 'sun-solid', 55000,
        'Held long enough, it stops mattering what the target was made of.',
        { grants: ['ramp:0.8:11.0'], damage: 26, mul: { damage: 1.2 } }),
    ],
    [
      n('Prism Array', 'shapes-solid', 230,
        'Splits one emitter into two aimed at two problems.',
        { grants: ['split:2'] }),
      n('Beam Splitter', 'branch-solid', 470,
        'Three beams, each at four fifths of the parent.',
        { grants: ['split:3'], damage: 2 }),
      n('Refraction Grid', 'grid-solid', 1450,
        'A lattice of micro-optics turns the emitter into a fan.',
        { grants: ['split:5'], damage: 4, range: 12 }),
      n('Diffraction Fan', 'align-center-solid', 7900,
        'Eight independent beams, no shared cooldown, no shared target.',
        { grants: ['split:8'], damage: 8, range: 16 }),
      n('Spectral Cascade', 'themes-solid', 62500,
        'Fourteen wavelengths, fourteen targets, one power bill.',
        { grants: ['split:14'], damage: 20, range: 26, mul: { damage: 1.15 } }),
    ],
    [
      n('Ablative Cutter', 'pen-nib-solid', 250,
        'Boils the outer layer off before the real damage lands.',
        { armourPierce: 0.2 }),
      n('Armour Melt', 'cloud-fog-solid', 500,
        'Every tick permanently softens the plate under the spot.',
        { armourPierce: 0.3, grants: ['dot:6:2'] }),
      n('Plasma Cutter', 'bolt-solid', 1550,
        'Stops being light and starts being a very small, very hot knife.',
        { armourPierce: 0.35, damage: 8, pierce: 1 }),
      n('Hull Breaker', 'octagon-times-solid', 8600,
        'Cuts a slot straight through the target and into the one behind it.',
        { armourPierce: 0.4, damage: 27, pierce: 3, grants: ['shieldMelt:2.0'] }),
      n('Mono-Molecular Edge', 'divider-solid', 67500,
        'One atom wide at the focus. Plate parts before it knows it was touched.',
        { armourPierce: 0.5, damage: 81, pierce: 8, grants: ['shieldMelt:4.0'], mul: { damage: 1.2 } }),
    ],
  ],
};

// ───────────────────────────────────────────────────────────────────── flak ──

const FLAK: TowerArchetype = {
  id: 'flak',
  name: 'Flak Battery',
  icon: 'shapes-solid',
  colour: 'lime',
  cost: 260,
  footprint: 7,
  blurb: 'Short-ranged cone of pellets. Deletes swarms, embarrasses itself against armour.',
  base: stats(3, 'kinetic', 1.3, 72, 6, 380, 0, 0.9),
  paths: [
    [
      n('Extra Shot', 'plus-solid', 110,
        'Three more pellets in the same shell. Free real estate.',
        { pierce: 3 }),
      n('Buckshot', 'ellipses-horizontal-solid', 230,
        'Fewer, fatter pellets. Each one now counts for something.',
        { pierce: 4, damage: 2 }),
      n('Flechette Load', 'bullet-list-solid', 710,
        'Finned darts instead of balls. They fly straight and land hard.',
        { pierce: 8, damage: 4, armourPierce: 0.25 }),
      n('Canister Shot', 'archive-solid', 3950,
        'The whole can goes downrange and opens on the way.',
        { pierce: 14, damage: 8, mul: { fireRate: 1.15 } }),
      n('Storm of Lead', 'cloud-rain-solid', 31000,
        'Thirty projectiles per pull. The cone is opaque.',
        { pierce: 30, damage: 16, mul: { damage: 1.15 } }),
    ],
    [
      n('Open Choke', 'expand-solid', 100,
        'Widens the pattern at the cost of nothing you cared about.',
        { grants: ['cone:0.42'] }),
      n('Wide Bore', 'bracket-round-solid', 210,
        'A wider throat throws a wider wall.',
        { grants: ['cone:0.55'], range: 8 }),
      n('Sweeping Arc', 'shuffle-solid', 670,
        'The mount traverses while firing. Nothing in front is safe.',
        { grants: ['cone:0.75'], range: 12, damage: 2 }),
      n('Full Spread', 'align-justify-solid', 3750,
        'A hundred degrees of coverage from one emplacement.',
        { grants: ['cone:0.95'], range: 16, damage: 5 }),
      n('Wall of Steel', 'bars-solid', 29500,
        'A hundred and sixty degrees. Walking past it is a decision.',
        { grants: ['cone:1.4'], range: 24, damage: 12, pierce: 6 }),
    ],
    [
      n('Timed Fuse', 'clock-solid', 130,
        'Bursts at a set distance rather than waiting for contact.',
        { grants: ['prox:10'], splash: 8 }),
      n('Proximity Detonator', 'wifi-solid', 270,
        'Radar-triggered. It knows how close is close enough.',
        { grants: ['prox:16'], splash: 12, damage: 2 }),
      n('Airburst Shells', 'cloud-upload-solid', 800,
        'Detonates above the formation and rains fragments down.',
        { grants: ['prox:22'], splash: 18, damage: 5, damageType: 'explosive' }),
      n('Flechette Burst', 'star-solid', 4400,
        'The burst itself throws a second cloud of darts outward.',
        { grants: ['prox:28'], splash: 24, damage: 12, pierce: 4 }),
      n('Shrapnel Storm', 'sparkles-solid', 34000,
        'Every burst seeds three more. The air stops being survivable.',
        { grants: ['prox:36', 'cluster:3:0.4'], splash: 32, damage: 26, mul: { damage: 1.15 } }),
    ],
  ],
};

// ───────────────────────────────────────────────────────────────────── acid ──

const ACID: TowerArchetype = {
  id: 'acid',
  name: 'Acid Sprayer',
  icon: 'cloud-rain-solid',
  colour: 'green',
  cost: 340,
  footprint: 7,
  blurb: 'Terrible burst, excellent patience. Everything it touches keeps dissolving.',
  base: stats(2, 'corrosive', 1.4, 84, 2, 240, 14),
  paths: [
    [
      n('Corrosive Bile', 'seedlings-solid', 150,
        'The first coat that actually keeps working after it lands.',
        { grants: ['dot:5:3', 'stacks:2'] }),
      n('Second Dose', 'copy-solid', 310,
        'Two stacks hold at once and they do not politely take turns.',
        { grants: ['dot:9:4', 'stacks:4'], damage: 1 }),
      n('Caustic Buildup', 'trending-solid', 940,
        'Each stack eats the residue of the last and gets stronger for it.',
        { grants: ['dot:18:5', 'stacks:6'], damage: 3 }),
      n('Compounding Acid', 'chart-line-solid', 5300,
        'Ten stacks. The number on screen stops being readable.',
        { grants: ['dot:38:6', 'stacks:10'], damage: 6, mul: { fireRate: 1.15 } }),
      n('Dissolution', 'trash-solid', 41000,
        'Nothing stays solid long enough to reach the exit.',
        { grants: ['dot:95:8', 'stacks:16'], damage: 14, mul: { damage: 1.2 } }),
    ],
    [
      n('Bursting Sacs', 'box-heart-solid', 170,
        'A dead host pops and coats whoever was walking too close.',
        { grants: ['spreadOnDeath:24:0.5'] }),
      n('Contagion', 'bug-solid', 340,
        'The splash carries the full stack count, not a polite fraction.',
        { grants: ['spreadOnDeath:34:0.75'], damage: 2 }),
      n('Viral Splash', 'share-alt-solid', 1050,
        'Spreads twice before it thins out. Swarms feed it.',
        { grants: ['spreadOnDeath:46:1.0'], splash: 8, damage: 4 }),
      n('Epidemic', 'medical-house-solid', 5850,
        'One kill in a tight group takes the whole group with it.',
        { grants: ['spreadOnDeath:62:1.2'], splash: 14, damage: 9 }),
      n('Grey Goo', 'cloud-fog-solid', 45000,
        'Self-replicating. You will want to check the leak counter anyway.',
        { grants: ['spreadOnDeath:88:1.6', 'dot:40:6'], splash: 22, damage: 20, mul: { damage: 1.15 } }),
    ],
    [
      n('Etching Agent', 'pencil-solid', 180,
        'Scores the plate so later hits have somewhere to bite.',
        { armourPierce: 0.25 }),
      n('Shield Solvent', 'unlock-solid', 360,
        'Overshields are a chemistry problem and this is the answer.',
        { grants: ['shieldMelt:2.5'], armourPierce: 0.3 }),
      n('Dielectric Breakdown', 'bolt-solid', 1100,
        'The emitter cannot hold a field while its housing is liquid.',
        { grants: ['shieldMelt:4.0'], armourPierce: 0.35, damage: 5 }),
      n('Shield Ripper', 'octagon-times-solid', 6150,
        'Strips the overshield faster than any regen can argue.',
        { grants: ['shieldMelt:6.5'], armourPierce: 0.4, damage: 18 }),
      n('Aegis Dissolver', 'times-square-solid', 48000,
        'Shields, armour, hull. It works down the list without pausing.',
        { grants: ['shieldMelt:12.0'], armourPierce: 0.5, damage: 54, mul: { damage: 1.15 } }),
    ],
  ],
};

// ─────────────────────────────────────────────────────────────────── sniper ──

const SNIPER: TowerArchetype = {
  id: 'sniper',
  name: 'Sniper Nest',
  icon: 'eye-solid',
  colour: 'white',
  cost: 600,
  footprint: 6,
  blurb: 'One shot at a time, aimed at whatever most deserves it, from anywhere.',
  base: stats(40, 'kinetic', 0.4, 260, 1, 1400, 0, 1.3, 0.10, 2.0),
  paths: [
    [
      n('Steady Hands', 'handshake-solid', 240,
        'Bipod, cheek weld, and a heart rate under fifty.',
        { critChance: 0.10, critMultiplier: 0.2 }),
      n('Vital Scan', 'medical-house-solid', 500,
        'Thermal overlay marks the part that matters most.',
        { critChance: 0.15, critMultiplier: 0.4, damage: 15 }),
      n('Headhunter', 'hockey-mask-solid', 1550,
        'Half of every magazine now lands somewhere fatal.',
        { critChance: 0.22, critMultiplier: 0.8, damage: 35 }),
      n('Executioner', 'trophy-solid', 8600,
        'Below a third health, it stops rolling and simply decides.',
        { critChance: 0.30, critMultiplier: 1.5, damage: 90, grants: ['ability:execute:30'] }),
      n('One Shot, One Kill', 'star-solid', 70000,
        'Every round is a critical. The word stops meaning anything.',
        { critChance: 1.0, critMultiplier: 2.0, damage: 260, mul: { critMultiplier: 1.15 } }),
    ],
    [
      n('Elevated Mount', 'arrow-up-solid', 220,
        'Height is range, and this one is on a mast.',
        { range: 60 }),
      n('Spotter Drone', 'plane-solid', 460,
        'A second pair of eyes, forty metres further out than yours.',
        { range: 110, mul: { fireRate: 1.2 } }),
      n('Satellite Uplink', 'wifi-solid', 1450,
        'Firing solutions arrive from orbit before the target clears cover.',
        { range: 200, damage: 30, mul: { fireRate: 1.2 } }),
      n('Orbital Designator', 'globe-solid', 7900,
        'Paints anything on the field. Range is now a formality.',
        { range: 400, damage: 70, grants: ['global'] }),
      n('Total Battlefield Awareness', 'globe-americas-solid', 65000,
        'It sees the whole map and it has opinions about all of it.',
        { range: 900, damage: 180, grants: ['global'], mul: { fireRate: 1.35 } }),
    ],
    [
      n('Tungsten Core', 'disc-solid', 260,
        'Dense enough that the plate gives up first.',
        { armourPierce: 0.3, damage: 12 }),
      n('AP Rounds', 'location-pin-solid', 540,
        'Purpose-built for the things everything else bounces off.',
        { armourPierce: 0.35, damage: 30 }),
      n('Depleted Uranium', 'exclamation-triangle-solid', 1650,
        'Self-sharpening on impact. Gets better the deeper it goes.',
        { armourPierce: 0.4, damage: 75, pierce: 1 }),
      n('Rail Slug', 'bolt-solid', 9250,
        'Magnetically launched, arrives before the crack does, exits the far side.',
        { armourPierce: 0.5, damage: 255, pierce: 3, projectileSpeed: 600 }),
      n('Fracture Round', 'text-slash-solid', 75000,
        'Cuts the plate away rather than punching through it. Nothing is proof.',
        { armourPierce: 0.65, damage: 756, pierce: 6, mul: { damage: 1.2 } }),
    ],
  ],
};

// ──────────────────────────────────────────────────────────────────── pylon ──

const PYLON: TowerArchetype = {
  id: 'pylon',
  name: 'Pylon',
  icon: 'sitemap-solid',
  colour: 'teal',
  cost: 450,
  footprint: 6,
  blurb: 'Fires nothing. Makes every tower in its ring worth more than it cost.',
  base: stats(0, 'arcane', 0, 100, 0, 0, 0),
  paths: [
    [
      n('Resonance Coil', 'wifi-solid', 200,
        'Every tower in the ring hits ten percent harder for free.',
        { grants: ['aura:damage:0.10'] }),
      n('Amplifier Node', 'sound-on-solid', 420,
        'Doubles the buff and reaches a little further to deliver it.',
        { grants: ['aura:damage:0.20'], range: 14 }),
      n('Harmonic Driver', 'music-solid', 1250,
        'Tunes each neighbour individually. Thirty-five percent, everywhere.',
        { grants: ['aura:damage:0.35'], range: 20 }),
      n('Overtune', 'trending-solid', 7050,
        'Runs the whole cluster past its rated output and dares it to complain.',
        { grants: ['aura:damage:0.55'], range: 26 }),
      n('Apex Resonance', 'crown-solid', 52500,
        'A ninety percent damage aura. Build the expensive towers next to it.',
        { grants: ['aura:damage:0.90'], range: 40, mul: { range: 1.1 } }),
    ],
    [
      n('Timing Crystal', 'clock-solid', 210,
        'Shaves the dead time out of every reload in range.',
        { grants: ['aura:rate:0.10'] }),
      n('Sync Pulse', 'refresh-solid', 440,
        'Aligns every neighbour to one clock and drops the jitter.',
        { grants: ['aura:rate:0.20'], range: 14 }),
      n('Cadence Loop', 'spinner-solid', 1300,
        'The cluster fires on a rolling beat instead of in ragged bursts.',
        { grants: ['aura:rate:0.32'], range: 20 }),
      n('Rapid Sync', 'spinner-third-solid', 7250,
        'Half again the rate of fire across everything you built nearby.',
        { grants: ['aura:rate:0.50'], range: 26 }),
      n('Chronal Overdrive', 'command-solid', 55000,
        'Doubles the rate of fire in the ring. Ammunition is your problem.',
        { grants: ['aura:rate:1.0', 'ability:overdrive:35'], range: 40, mul: { range: 1.1 } }),
    ],
    [
      n('Salvage Beacon', 'coin-solid', 190,
        'Neighbours strip the wrecks for eight percent more cash.',
        { grants: ['aura:cash:0.08'] }),
      n('Tithe Collector', 'coins-solid', 400,
        'A cut of everything killed inside the ring, paid instantly.',
        { grants: ['aura:cash:0.18'], range: 14 }),
      n('Profit Sharing', 'box-usd-solid', 1200,
        'A third again on every pop, and the ring covers half your defence.',
        { grants: ['aura:cash:0.33'], range: 22 }),
      n('Bull Market', 'chart-line-solid', 6600,
        'Sixty percent bonus income. The economy becomes the strategy.',
        { grants: ['aura:cash:0.60'], range: 28 }),
      n('Central Bank', 'bank-solid', 49000,
        'Doubles the take inside the ring and pays a dividend each round.',
        { grants: ['aura:cash:1.0', 'ability:dividend:40'], range: 44, mul: { range: 1.15 } }),
    ],
  ],
};

// ────────────────────────────────────────────────────────────── singularity ──

const SINGULARITY: TowerArchetype = {
  id: 'singularity',
  name: 'Singularity',
  icon: 'circle-notch-solid',
  colour: 'purple',
  cost: 800,
  footprint: 9,
  blurb: 'Drops a gravity well onto the track. Pulls the swarm into a single point and crushes it.',
  base: stats(6, 'arcane', 0.6, 120, 99, 200, 46),
  paths: [
    [
      n('Gravity Coil', 'circle-notch-solid', 320,
        'The well now actually pulls instead of merely suggesting.',
        { grants: ['pull:220'] }),
      n('Tidal Lock', 'lock-solid', 660,
        'Anything caught stops making its own decisions about direction.',
        { grants: ['pull:400', 'slow:0.75:1.0'], damage: 3 }),
      n('Deep Well', 'download-solid', 1950,
        'Drags the whole column backward down the track.',
        { grants: ['pull:700', 'slow:0.6:1.4'], damage: 8, splash: 10 }),
      n('Inescapable Draw', 'merge-solid', 10500,
        'Escape velocity now exceeds anything on the enemy list.',
        { grants: ['pull:1200', 'slow:0.45:2.0'], damage: 18, splash: 16 }),
      n('Frame Drag', 'shuffle-solid', 80000,
        'Rotates the space around it. Nothing crosses without being rearranged.',
        { grants: ['pull:2200', 'slow:0.3:2.6'], damage: 45, splash: 28, mul: { range: 1.15 } }),
    ],
    [
      n('Accretion Disc', 'disc-solid', 340,
        'Material caught in orbit heats up and starts doing your job for you.',
        { damage: 8, splash: 8 }),
      n('Tidal Shear', 'divider-solid', 700,
        'The gradient across a body is enough to pull it apart.',
        { damage: 20, splash: 12, armourPierce: 0.3 }),
      n('Spaghettification', 'line-height-solid', 2150,
        'Length increases, structural integrity does not.',
        { damage: 48, splash: 18, armourPierce: 0.35, mul: { fireRate: 1.15 } }),
      n('Hawking Radiation', 'sparkles-solid', 11500,
        'The horizon itself is now hot, and it never turns off.',
        { damage: 172, splash: 26, armourPierce: 0.4, grants: ['dot:60:4'] }),
      n('Ergosphere', 'globe-solid', 87500,
        'Anything inside the boundary is already losing energy to the well.',
        { damage: 540, splash: 38, armourPierce: 0.55, grants: ['dot:180:5'], mul: { damage: 1.2 } }),
    ],
    [
      n('Collapse Charge', 'times-circle-solid', 350,
        'The well no longer fades. It ends, loudly.',
        { grants: ['detonate:1.5:50'] }),
      n('Implosion', 'collapse-solid', 720,
        'Everything it gathered arrives at the centre simultaneously.',
        { grants: ['detonate:2.5:66'], damage: 12 }),
      n('Nova Collapse', 'star-solid', 2200,
        'The inward rush overshoots and comes back out as a wave.',
        { grants: ['detonate:4.0:88', 'stun:0.5', 'stunChance:1.0'], damage: 30, splash: 10 }),
      n('Supermassive', 'moon-solid', 12000,
        'A detonation radius wider than most towers can see.',
        { grants: ['detonate:6.5:120'], damage: 75, splash: 20, mul: { splash: 1.15 } }),
      n('Big Rip', 'exclamation-triangle-solid', 90000,
        'Clears the screen. Then you check whether the lattice survived.',
        { grants: ['detonate:11.0:170', 'ability:bigrip:45'], damage: 200, splash: 40, mul: { damage: 1.2, splash: 1.2 } }),
    ],
  ],
};

// ───────────────────────────────────────────────────────────────── registry ──

export const TOWERS: readonly TowerArchetype[] = [
  TURRET, MORTAR, TESLA, CRYO, LASER, FLAK, ACID, SNIPER, PYLON, SINGULARITY,
];

/**
 * Late-tier cost inflation, applied once at module load.
 *
 * Acts raised the widest board from twenty-four slots to forty-eight
 * (`Levels.ACTS[6].slots`), which doubles what a finished board costs and
 * therefore doubles how much income the economy can absorb before the tree is
 * exhausted. Left alone, that headroom went straight into *depth* — the model's
 * act VII board reached tier five on most of forty-eight towers and cleared its
 * rounds at coverage 2.3 against a target of 1.35.
 *
 * A flat multiplier on tiers four and five is the correction, and it is the
 * right shape rather than merely the convenient one. §4.3's argument for a steep
 * capstone was that a repeatable sink flattens every decision back into "how
 * much money do I have"; widening the board without steepening the top does
 * exactly that, because forty-eight slots is itself close to a repeatable sink.
 * Keeping the ratio between board width and capstone price roughly fixed is what
 * keeps "which two towers do I finish" the question it was at twenty-four slots.
 *
 * Tiers one to three are untouched, so nothing about acts I–III's pricing moved.
 */
const LATE_TIER_COST: readonly number[] = [1, 1, 1, 1.35, 1.35];

for (const t of TOWERS) {
  for (const path of t.paths) {
    for (let i = 0; i < path.length; i++) {
      path[i].cost = Math.round(path[i].cost * (LATE_TIER_COST[i] ?? 1));
    }
  }
}

const BY_ID = new Map<string, TowerArchetype>();
for (const t of TOWERS) BY_ID.set(t.id, t);

export function towerById(id: string): TowerArchetype {
  const t = BY_ID.get(id);
  if (!t) throw new Error(`unknown tower archetype: ${id}`);
  return t;
}

/**
 * Firing verbs and sprite bindings, keyed by archetype id. `sprite` indexes the
 * barrel atlas built by `render/TowerRenderer.ts`; `plate` picks one of four
 * base-plate designs by weight class.
 */
export const BEHAVIOUR: Readonly<Record<string, TowerBehaviour>> = {
  turret: { kind: 'projectile', cone: 0, chain: 0, chainFalloff: 0, homing: 0, muzzle: 7, turnSeconds: 0.16, sprite: 0, plate: 1, trailed: false },
  mortar: { kind: 'ground', cone: 0, chain: 0, chainFalloff: 0, homing: 0, muzzle: 6, turnSeconds: 0.45, sprite: 1, plate: 2, trailed: true },
  tesla: { kind: 'chain', cone: 0, chain: 1, chainFalloff: 0.65, homing: 0, muzzle: 5, turnSeconds: 0.10, sprite: 2, plate: 1, trailed: false },
  cryo: { kind: 'cone', cone: 0.34, chain: 0, chainFalloff: 0, homing: 0, muzzle: 6, turnSeconds: 0.22, sprite: 3, plate: 1, trailed: false },
  laser: { kind: 'beam', cone: 0, chain: 0, chainFalloff: 0, homing: 0, muzzle: 7, turnSeconds: 0.08, sprite: 4, plate: 1, trailed: false },
  flak: { kind: 'cone', cone: 0.30, chain: 0, chainFalloff: 0, homing: 0, muzzle: 6, turnSeconds: 0.20, sprite: 5, plate: 0, trailed: false },
  acid: { kind: 'projectile', cone: 0, chain: 0, chainFalloff: 0, homing: 0.4, muzzle: 6, turnSeconds: 0.20, sprite: 6, plate: 0, trailed: true },
  sniper: { kind: 'projectile', cone: 0, chain: 0, chainFalloff: 0, homing: 0, muzzle: 8, turnSeconds: 0.30, sprite: 7, plate: 0, trailed: true },
  pylon: { kind: 'none', cone: 0, chain: 0, chainFalloff: 0, homing: 0, muzzle: 0, turnSeconds: 1.0, sprite: 8, plate: 3, trailed: false },
  singularity: { kind: 'well', cone: 0, chain: 0, chainFalloff: 0, homing: 0.9, muzzle: 6, turnSeconds: 0.35, sprite: 9, plate: 3, trailed: true },
};

export function behaviourOf(id: string): TowerBehaviour {
  const b = BEHAVIOUR[id];
  if (!b) throw new Error(`no behaviour registered for tower: ${id}`);
  return b;
}

/** Total upgrade nodes in the catalogue. Asserted by the test suite. */
export const NODE_COUNT = TOWERS.reduce(
  (sum, t) => sum + t.paths[0].length + t.paths[1].length + t.paths[2].length, 0,
);

/** Highest tier any path can reach. The crossover rule lives in `Upgrades.ts`. */
export const MAX_TIER = 5;
